import React from 'react';
import moment from "moment-timezone";
import { MenuItem, Typography, Box, Button, Select, FormControl, Chip, TextField, Autocomplete  } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import MaterialReactTable from 'material-react-table';
import colors from "assets/mavric-theme/base/colors.js";
import typography from "assets/mavric-theme/base/typography";
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import borders from "assets/mavric-theme/base/borders";
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { downloadAllUserActivitiesReport, getAllCustomerUserActivities, getDataconsumptionReportTypes } from 'apis/CustomersApis';
const { primary, secondary, text, tableRow, transparent } = colors;
const { size } = typography;
const { borderRadius } = borders;
const ITEM_HEIGHT = 48;

const ViewAllUserActivity = () => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const [isLoading, setIsLoading] = React.useState(false);
    const [allUserActivities, setAllUserActivities] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [orgNameOptions, setOrgNameOptions] = React.useState([]);
    const [displayedView, setDisplayedView] = React.useState('thismonth');
    const [reportTypes, setReportTypes] = React.useState([]);
    const [currentView, setCurrentView] = React.useState('thismonth');
    const [downloading, setDownloading] = React.useState(false);
    const [customerIdOptions, setCustomerIdOptions] = React.useState([]);
    const [timezone] = React.useState(userTimeZone);
    const [downloadDialog, dispatch] = React.useReducer((state, action) => {
        switch (action.type) {
            case "SET_FILENAME":
            return { ...state, filename: action.payload };
            case "SET_OPEN":
            return { ...state, open: action.payload };
            case "SET_BLOB":
            return { ...state, blob: action.payload };
            case "RESET":
            return { filename: "default", open: false, blob: null };
            default:
            return state;
        }
    }, { filename: "default", open: false, blob: null });
    const columns = React.useMemo(() => [

        {
            accessorKey: 'customer_id',
            header: 'Customer ID',
            size: 130,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value || '-';
            },
            Filter: ({ header }) => (
                <Autocomplete
                    size="small"
                    options={customerIdOptions}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option}
                    renderOption={(props, option, { selected }) => (
                        <MenuItem {...props} sx={{ "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus": { backgroundColor: `${tableRow.hover} !important` } }}> {option}</MenuItem>
                    )}
                    style={{ width: 130 }}
                    renderInput={(params) => (
                        <TextField {...params} variant="outlined" placeholder="Customer ID" />
                    )}
                    sx={{ '& .MuiAutocomplete-popupIndicator, & .MuiSvgIcon-root': { color: secondary.main } }}
                    onChange={(event, value) =>
                        header.column.setFilterValue(value)}
                    clearIcon={<HighlightOffIcon fontSize="small" />}
                    ListboxProps={{
                        sx: {
                            maxHeight: ITEM_HEIGHT * 4.5,
                        }
                    }}
                />
            ),
            filterFn: 'customerIdFilterFn',

        },
        {
            accessorKey: 'org_name',
            header: 'Customer Name',
            size: 150,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value || '-';
            },
            Filter: ({ header }) => (
                <Autocomplete
                    size="small"
                    options={orgNameOptions}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option}
                    renderOption={(props, option, { selected }) => (
                        <MenuItem {...props} sx={{ "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus": { backgroundColor: `${tableRow.hover} !important` } }}> {option}</MenuItem>
                    )}
                    style={{ width: 150 }}
                    renderInput={(params) => (
                        <TextField {...params} variant="outlined" placeholder="Customer Name" />
                    )}
                    sx={{ '& .MuiAutocomplete-popupIndicator, & .MuiSvgIcon-root': { color: secondary.main } }}
                    onChange={(event, value) =>
                        header.column.setFilterValue(value)}
                    clearIcon={<HighlightOffIcon fontSize="small" />}
                    ListboxProps={{
                        sx: {
                            maxHeight: ITEM_HEIGHT * 4.5,
                        }
                    }}
                />
            ),
            filterFn: 'customerNameFilterFn',
        },
        {
            accessorKey: 'email',
            header: 'User Email',
            size: 200,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value || '-';
            },
            enableColumnFilter: false
        },
        {
            accessorKey: 'first_name',
            header: 'User First Name',
            size: 130,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value || '-';
            },
            enableColumnFilter: false
        },
        {
            accessorKey: 'last_name',
            header: 'User Last Name',
            size: 130,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value || '-';
            },
            enableColumnFilter: false
        },
        {
            accessorKey: 'user_agent',
            header: 'User App',
            size: 100,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value || '-';
            },
            enableColumnFilter: false
        },
        {
            accessorKey: 'remote_addr',
            header: 'IP',
            size: 100,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value || '-';
            },
            enableColumnFilter: false
        },
        {
            accessorKey: 'first_activity_on',
            header: 'First Activity On',
            size: 150,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value ? moment.utc(value).tz(userTimeZone).format("YYYY-MM-DD HH:mm:ss A") : '-';
            },
            enableColumnFilter: false
        },
        {
            accessorKey: 'last_activity_on',
            header: 'Last Activity On',
            size: 150,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value ? moment.utc(value).tz(userTimeZone).format("YYYY-MM-DD HH:mm:ss A") : '-';
            },
            enableColumnFilter: false
        },
        {
            accessorKey: 'is_active',
            header: 'Is Active',
            size: 100,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return (
                    <span className={value ? 'text-success' : 'text-danger'}>
                    {value ? 'Active' : 'Inactive'}
                    </span>
                );
            },
            enableColumnFilter: false
        },
        {
            accessorKey: 'total_sessions',
            header: 'Total Sessions',
            size: 100,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value !== null && value !== undefined ? value : '-';
            },
            enableColumnFilter: false
        },

    ], []);

    const downloadReport = async () => {
        if (!currentView) return;
        setDownloading(true);
        try {
            const periodMap = {
            'thismonth': 'thismonth',
            'lastmonth': 'lastmonth',
            'all': 'all'
            };
            const payload = {
            query_timezone: timezone,
            period: periodMap[displayedView],
            };
            // console.log('Download response:~bj', response);
            const period = periodMap[displayedView];
            const blob = await downloadAllUserActivitiesReport(payload);
            const filename = getUserActivitiesFileName(period);
            // console.log('Download filename:~bj', filename);
            dispatch({ type: "SET_BLOB", payload: blob });
            dispatch({ type: "SET_FILENAME", payload: filename });
            dispatch({ type: "SET_OPEN", payload: true });

        } catch (error) {
            console.error('Download error:', error);
        } finally {
            setDownloading(false);
        }
    };


    const getDisplayLabel = (value) => {
        const labels = {
            'thismonth': 'This Month',
            'lastmonth': 'Last Month',
            'all': 'All'
        };
        return labels[value] || value;
    };

    const fetchAllUserActivities = async (period = currentView) => {
        setIsLoading(true);
        // BJ
        // const periodMap = { 'thismonth': 'thismonth', 'lastmonth': 'lastmonth', 'all': 'all' };
        // const periodStock = periodMap[currentView];
        try {
            const response = await getAllCustomerUserActivities(period);
            if (!response || !Array.isArray(response)) {
                console.warn("Invalid response format:", response);
                setAllUserActivities([]);
                setDisplayedView(currentView);
                return;
            }
            setAllUserActivities(response);
            setDisplayedView(currentView);
        } catch (error) {
            console.error('Error fetching user activities:', error);
            setAllUserActivities([]);
        } finally {
            setIsLoading(false);
        }
    };


    const fetchReportTypesData = async () => {
        setLoading(true);
        try {
          const response = await getDataconsumptionReportTypes();
          // console.log("Data loaded:", response);
          const types = response && response.report_types
          const formattedTypes = types.map(type => ({
            value: type,
            label: type === 'thismonth' ? 'This Month' :
            type === 'lastmonth' ? 'Last Month' :
            type === 'all' ? 'All' : type
          }));
          setReportTypes(types);
    
        } catch (error) {
          console.error('Error:', error);
          setReportTypes([]);
        } finally {
          setLoading(false);
        }
    };

    const handleFilenameChange = React.useCallback((e) => dispatch({ type: "SET_FILENAME", payload: e.target.value }), []);

    const handleCloseDialog = React.useCallback(() => dispatch({ type: "SET_OPEN", payload: false }), []);

    const handleSelectionChange = (event) => { const selectedValue = event.target.value; setCurrentView(selectedValue); };

    const getViewLabelWithMonth = (view) => {
        const date = new Date();
        if (view === 'thismonth') {
        const monthName = date.toLocaleString('default', { month: 'long', year: 'numeric' });
        return monthName;
        } else if (view === 'lastmonth') {
        date.setMonth(date.getMonth() - 1);
        const monthName = date.toLocaleString('default', { month: 'long', year: 'numeric' });
        return monthName;
        } else if (view === 'all') {
        return 'All';
        }
        return 'Select Period';
    };

    const getUserActivitiesFileName = (period) => {
        const date = new Date();
        const year = date.getFullYear();
        if (period === 'all') {
        return `MX_AllUserActivities_Report_${year}.xlsx`;
        } else {
        if (period === 'lastmonth') date.setMonth(date.getMonth() - 1);
        const monthName = date.toLocaleString('default', { month: 'short' });
        const monthYear = date.getFullYear();
        return `MX_AllUserActivities_Report_${monthName}_${monthYear}.xlsx`;
        }
    };

    React.useEffect(() => {
        if (Array.isArray(allUserActivities) && allUserActivities.length > 0)  prepareAllUsersData(allUserActivities);
    }, [allUserActivities]);

    const prepareAllUsersData = (data) => {
        let updtdOrgNameOptions = orgNameOptions;
        let updtdCustIdOptions = customerIdOptions;
        data.map(row => {
            const isDuplicateOrgName = updtdOrgNameOptions.includes(row.org_name);
            const isDuplicateCustId = updtdCustIdOptions.includes(row.customer_id);
            if (!isDuplicateOrgName) updtdOrgNameOptions.push(row.org_name);
            if (!isDuplicateCustId) updtdCustIdOptions.push(String(row.customer_id));
        });
        setOrgNameOptions(updtdOrgNameOptions.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
        setCustomerIdOptions(updtdCustIdOptions.sort(((a, b) => parseInt(a) - parseInt(b))));
    };


    const handleConfirmDownload = () => {
        if (downloadDialog.blob && downloadDialog.filename) {
            const url = window.URL.createObjectURL(downloadDialog.blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = downloadDialog.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }
        dispatch({ type: "RESET" });
    };


    React.useEffect(() => {
        fetchAllUserActivities();
        fetchReportTypesData();
    }, []);

    return (
        <>
            <Box sx={{ mt: 3, '& .MuiPaper-root': { borderRadius: 3, overflow: 'hidden' } }}>
                <MaterialReactTable
                    key={`all-users-activity`}
                    columns={columns}
                    data={allUserActivities}
                    state={{ isLoading }}
                    muiTableToolbarAlertBannerProps={{ sx: { display: 'none' } }}
                    muiTablePaperProps={{ sx: { boxShadow: 'none', backgroundColor: primary.main, p: 2 } }}
                    muiTableBodyRowProps={({ row }) => ({ hover: true,sx: { color: text.main, align: 'center', '&:nth-of-type(odd)': { backgroundColor: tableRow.dark, border: 0 }, '&:nth-of-type(even)': { background: tableRow.light }, '&:last-child td, &:last-child th': { border: 0 }, '&:hover td': { background: tableRow.hover }, '& td': { lineHeight: 2.1 } },})}
                    muiTableBodyCellProps={{ sx: { border: 'none', color: text.main, align: 'center', boxShadow: 'none', padding: '4px 8px', } }}
                    muiTableProps={{ sx: { backgroundColor: primary.main, color: text.main, boxShadow: 'none' } }}
                    muiTopToolbarProps={{ sx: { backgroundColor: primary.main, boxShadow: 'none', minHeight: 0 } }}
                    muiBottomToolbarProps={{ sx: { backgroundColor: primary.main, boxShadow: 'none' } }}
                    muiTableHeadRowProps={{ sx: { backgroundColor: primary.main, color: text.main, textAlign: 'center' }, }}
                    muiTableHeadCellProps={{ sx: { borderBottom: 0, verticalAlign: 'none', boxShadow: 'none', align: 'center', color: text.main } }}
                    enableColumnActions={false}
                    enableColumnOrdering={false}
                    enablePinning={false}
                    enableSelectAll={false}
                    enableStickyHeader={false}
                    enableClickToCopy={false}
                    positionPagination="bottom"
                    enableToolbarInternalActions={false}
                    enablePagination={true}
                    initialState={{ density: 'compact', showColumnFilters: true, boxShadow: 'none', pagination: { pageSize: 15, pageIndex: 0, pageCount: -1 }, sorting: [ { id: 'email', desc: false, } ], }}
                    positionActionsColumn="last"
                    muiTablePaginationProps={{ rowsPerPageOptions: [3, 5, 10, 15, 20], sx: { color: text.main, '& p': { marginBottom: 0 }, '& .MuiSvgIcon-root': { color: text.main, }, } }}
                    muiTableContainerProps={{ sx: { padding: '0 !important' } }}
                    enableSorting={true}
                    renderTopToolbar={({ table }) => (
                        <Box sx={{ display: 'flex', gap: 2, p: 0.5, pl: 0, pr: 0, justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', gap: 2, }}>
                                <FormControl size="medium" sx={{ minWidth: 150 }}>
                                    <Select value={currentView} onChange={handleSelectionChange} sx={{ '& .MuiSelect-select': { paddingTop: '8px !important', paddingBottom: '8px !important', minHeight: '30px !important', display: 'flex', alignItems: 'center', py: 1, fontSize: '0.875rem !important' }, '& .MuiSvgIcon-root': { width: 22, height: 22, top: 10 } }} >{reportTypes.map((item, index) => (  <MenuItem key={`timePeriods${index}`} value={item}> {getDisplayLabel(item)} </MenuItem> ))}</Select>
                                </FormControl>
                                <Button onClick={() => fetchAllUserActivities(currentView)} variant="contained" sx={{ '&:hover': { backgroundColor: secondary.main, }, }} color="secondary" size="medium" startIcon={<RefreshIcon />} disabled={!allUserActivities || allUserActivities.length === 0 && isLoading} > {isLoading ? 'fetching...' : 'Fetch Data'} </Button>
                                <Button onClick={downloadReport} variant='contained' color="secondary" size="medium" startIcon={<DownloadIcon sx={{  color: (!allUserActivities || allUserActivities.length === 0)  ? 'secondary.main'  : 'inherit' }} />} disabled={!allUserActivities || allUserActivities.length === 0} > {downloading ? 'Downloading...' : 'Download Report'} </Button>
                            </Box>
                            <Box sx={{ display: 'flex' }}>  <Chip label={getViewLabelWithMonth(displayedView)} color="secondary" variant="outlined" size="medium" sx={{'& .MuiChip-label': { fontSize: '1rem', fontWeight: 600 } }} /> </Box>
                        </Box>
                    )}
                    filterFns={{
                        customerIdFilterFn: (row, _columnIds, filterValue) => {
                        if (filterValue !== null) {
                            if (filterValue !== 'All') {
                                return String(row.getValue('customer_id')) === filterValue;
                            } else {
                                return true;
                            }
                        } else {
                            return true;
                        }
                        },
                        customerNameFilterFn: (row, _columnIds, filterValue) => {
                        if (filterValue !== null) {
                            if (filterValue !== 'All') {
                                return row.getValue('org_name') === filterValue;
                            } else {
                                return true;
                            }
                        } else {
                            return true;
                        }
                        },
                    }}
                />
            </Box>

            <Dialog open={downloadDialog.open} onClose={handleCloseDialog} maxWidth="sm" aria-labelledby="scroll-dialog-title" aria-describedby="scroll-dialog-description" sx={{ '& .MuiDialog-paper': { backgroundColor: primary.main, } }} fullWidth >
            <DialogTitle id="alert-dialog-title"> <Typography fontSize={size.lg} color={secondary.main} sx={{ fontWeight: 600, }}>{'Confirm Download'}</Typography> </DialogTitle>
            <DialogContent>
                <Typography variant="body2" fontSize={size.sm} justifyContent="center" color={text.main} sx={{ fontWeight: 400, }}>Filename</Typography>
                <TextField type="text" fullWidth focused={false} value={downloadDialog.filename} onChange={handleFilenameChange} sx={{ color: text.main, backgroundColor: transparent.main, borderRadius: borderRadius.md, }} />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseDialog} variant="contained" color="secondary"> Cancel </Button>
                <Button onClick={handleConfirmDownload} color="secondary" variant="contained" disabled={!downloadDialog.filename.trim()}> Download </Button>
            </DialogActions>
            </Dialog>
        </>
    )
}

export default ViewAllUserActivity
